const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

const chartYAxisCompactFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

const chartYAxisCompactFormatters: Record<0 | 1 | 2, Intl.NumberFormat> = {
  0: new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }),
  1: new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
  }),
  2: chartYAxisCompactFormatter,
};

type ChartYAxisCompactSuffix = {
  value: number;
  suffix: string;
};

const CHART_Y_AXIS_COMPACT_SUFFIXES = [
  { value: 1e9, suffix: "b" },
  { value: 1e6, suffix: "m" },
  { value: 1e3, suffix: "k" },
] satisfies ChartYAxisCompactSuffix[];

function getChartYAxisCompactFractionDigits(compactValue: number): 0 | 1 | 2 {
  if (Math.abs(compactValue) >= 100) {
    return 0;
  }

  if (Math.abs(compactValue) >= 10) {
    return 1;
  }

  return 2;
}

function roundToFractionDigits(value: number, fractionDigits: number): number {
  const multiplier = 10 ** fractionDigits;
  return Math.round(value * multiplier) / multiplier;
}

function formatChartYAxisCompactValue(value: number): string {
  const absoluteValue = Math.abs(value);

  for (let index = 0; index < CHART_Y_AXIS_COMPACT_SUFFIXES.length; index += 1) {
    const suffixConfig = CHART_Y_AXIS_COMPACT_SUFFIXES[index];
    if (!suffixConfig || absoluteValue < suffixConfig.value) {
      continue;
    }

    const compactValue = value / suffixConfig.value;
    const fractionDigits = getChartYAxisCompactFractionDigits(compactValue);
    const roundedCompactValue = roundToFractionDigits(compactValue, fractionDigits);

    if (Math.abs(roundedCompactValue) >= 1000 && index > 0) {
      const promotedSuffixConfig = CHART_Y_AXIS_COMPACT_SUFFIXES[index - 1];
      if (promotedSuffixConfig) {
        const promotedCompactValue = value / promotedSuffixConfig.value;
        const promotedFractionDigits = getChartYAxisCompactFractionDigits(promotedCompactValue);
        return `${chartYAxisCompactFormatters[promotedFractionDigits].format(
          promotedCompactValue
        )}${promotedSuffixConfig.suffix}`;
      }
    }

    return `${chartYAxisCompactFormatters[fractionDigits].format(compactValue)}${
      suffixConfig.suffix
    }`;
  }

  return numberFormatter.format(value);
}

export function formatMetricValue(value: number | null, unit?: string | null): string {
  if (value === null) {
    return "--";
  }

  if (unit === "Messages/Second") {
    return `${numberFormatter.format(value)} msg/s`;
  }

  if (unit === "Rows/Second") {
    return `${numberFormatter.format(value)} rows/s`;
  }

  if (unit === "Queries/Second") {
    return `${numberFormatter.format(value)} queries/s`;
  }

  if (unit === "Count/Second") {
    return `${numberFormatter.format(value)}/s`;
  }

  if (unit === "ECPUs/Second") {
    return `${numberFormatter.format(value)} ECPU/s`;
  }

  if (unit === "Megabytes") {
    return `${numberFormatter.format(value)} MB`;
  }

  if (unit === "Seconds") {
    return `${numberFormatter.format(value)}s`;
  }

  if (unit === "Percent") {
    return `${numberFormatter.format(value)}%`;
  }

  return numberFormatter.format(value);
}

export function formatChartYAxisValue(value: number | null): string {
  if (value === null) {
    return "";
  }

  return formatChartYAxisCompactValue(value);
}
